import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Activity as ActivityIcon, Search, Globe, Network, ShieldCheck, FileSearch, Gauge, UserCog, LogOut, ShoppingCart, History } from "lucide-react";
import { useConfig } from "@/context/ConfigContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const items = [
  { to: "/status", label: "Status", icon: Gauge, testid: "nav-status" },
  { to: "/search", label: "Domain Search", icon: Search, testid: "nav-search" },
  { to: "/buy", label: "Buy Domain", icon: ShoppingCart, testid: "nav-buy" },
  { to: "/domains", label: "My Domains", icon: Globe, testid: "nav-domains" },
  { to: "/dns", label: "DNS Manager", icon: Network, testid: "nav-dns" },
  { to: "/whois-update", label: "WHOIS Update", icon: UserCog, testid: "nav-whois-update" },
  { to: "/ssl", label: "SSL Certs", icon: ShieldCheck, testid: "nav-ssl" },
  { to: "/whois", label: "WHOIS Lookup", icon: FileSearch, testid: "nav-whois" },
  { to: "/history", label: "Purchase History", icon: History, testid: "nav-history" },
];

const Sidebar = () => {
  const { config } = useConfig();
  const { logout } = useAuth();
  const navigate = useNavigate();
  return (
    <aside
      data-testid="sidebar"
      className="w-[260px] shrink-0 bg-white border-r border-[var(--border)] flex flex-col h-screen sticky top-0"
    >
      <div className="px-6 py-6 border-b border-[var(--border)]">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 brand-bg rounded-sm flex items-center justify-center">
            <ActivityIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)]">Namecheap</div>
            <div className="font-semibold tracking-tight text-[15px]">Control Panel</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5">
        {items.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm rounded-sm transition-colors ${
                isActive
                  ? "bg-[#F4F4F5] text-[var(--text-primary)] font-medium border-l-2 border-[var(--brand)]"
                  : "text-[var(--text-secondary)] hover:bg-[#F4F4F5] hover:text-[var(--text-primary)] border-l-2 border-transparent"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={1.75} />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-[var(--border)] space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-1">
            Environment
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                config?.configured ? (config?.sandbox ? "bg-yellow-500" : "bg-green-600") : "bg-red-500"
              }`}
              data-testid="env-indicator"
            />
            <span className="text-sm mono">
              {!config?.configured ? "Not configured" : config?.sandbox ? "SANDBOX" : "PRODUCTION"}
            </span>
          </div>
        </div>
        <Button
          data-testid="btn-logout"
          variant="ghost"
          size="sm"
          onClick={() => { logout(); navigate("/login"); }}
          className="w-full justify-start rounded-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
